import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { ReportType } from '@/app/constants/reportType'
import {
  getReportNumberByType,
  getReportTypeByNumber,
} from '@/app/services/printout/reportTypeResolver'

type TimesheetPrintoutConfigurationDto = {
  reportTypes: number[]
  availableReportTypes: number[]
  maxReports: number
}

export type TimesheetPrintoutConfiguration = {
  reportTypes: ReportType[]
  availableReportTypes: ReportType[]
  maxReports: number
}

const QUERY_KEY = 'timesheetPrintoutConfiguration'

const toReportTypes = (numbers: number[] | undefined) =>
  (numbers ?? [])
    .map(getReportTypeByNumber)
    .filter((type) => type !== ReportType.NONE)

const toModel = (
  dto: TimesheetPrintoutConfigurationDto,
): TimesheetPrintoutConfiguration => ({
  reportTypes: toReportTypes(dto.reportTypes),
  availableReportTypes: toReportTypes(dto.availableReportTypes),
  maxReports: dto.maxReports,
})

/** Szybkie wydruki w pasku ewidencji - konfiguracja bazy firmy, wspólna z WPF. */
export const useTimesheetPrintoutConfiguration = (companyId: string) =>
  useQuery({
    queryKey: [QUERY_KEY, companyId],
    queryFn: async () => {
      const { data } = await axiosInstance.get<TimesheetPrintoutConfigurationDto>(
        `/api/${companyId}/printout/timesheetConfiguration`,
      )
      return toModel(data)
    },
    enabled: !!companyId,
  })

export const useSaveTimesheetPrintoutConfiguration = (companyId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (reportTypes: ReportType[]) => {
      const { data } = await axiosInstance.put<TimesheetPrintoutConfigurationDto>(
        `/api/${companyId}/printout/timesheetConfiguration`,
        { reportTypes: reportTypes.map(getReportNumberByType) },
      )
      return toModel(data)
    },
    onSuccess: (configuration) => {
      queryClient.setQueryData([QUERY_KEY, companyId], configuration)
    },
  })
}
